import { User } from "@supabase/supabase-js";
import { makeAutoObservable } from "mobx";
import { wrap } from "../util/supabase-wrapper";
import { Class } from "./class";
import { EngagementPanel } from "./engagementPanel";
import { Module } from "./module";
import { supabase, definitions } from "./supabase/client";

//TODO: Implement checks for whether user is logged in
export class Teacher {
  user: Partial<User> = {};
  staffDetail: Partial<definitions["StaffDetails"]> = {};
  teacherAccount: Partial<definitions["TeacherAccount"]> = {};
  //TODO: Test cases for this
  myClasses: Class[] = [];

  constructor() {
    makeAutoObservable(this);
    this.pullUserDetails();
  }

  static async getAllTeachers() {
    const teachers = wrap(
      await supabase
        .from("TeacherAccount")
        .select("*, staffDetail:StaffDetails(*)")
    );

    return teachers;
  }

  async init(id: number) {
    this.teacherAccount.id = id;
    await this.pullUserDetails();
    return this;
  }

  private updateClasses(newClasses: Class[]) {
    this.myClasses = newClasses;
  }

  //TODO: Make sure that the
  async getTeacherFeedbacks() {
    let teacherFeedbacks = wrap(
      await supabase
        .from("TeacherFeedback")
        .select(
          "*, TalkingPartyRoom(*,TimeSlot(*,Class(*,ClassTeacher(*,TeacherAccount(*,StaffDetails(*))))))"
        )
    );
    if (!this.staffDetail.isAdmin) {
      //if user is not admin, then only show feedback by themself.
      //@ts-ignore
      teacherFeedbacks = teacherFeedbacks.filter(
        (feedback) =>
          //@ts-ignore
          feedback.TalkingPartyRoom.TimeSlot.Class.ClassTeacher[0].teacher ===
          this.teacherAccount.id
      );
    }

    return teacherFeedbacks;
  }

  //TODO: Refactor this function to return this for consistency
  async getMyClasses() {
    if (!this.teacherAccount.id) throw Error("Teacher Account ID not defined");

    //if undefined is pass into getAllClasses, then it will return all classes, which is that desired behaviour for an admin account.
    // console.log(this.staffDetail.isAdmin)
    const myClasses = await Class.getAllClasses(
      this.staffDetail.isAdmin ? undefined : this.teacherAccount.id
    );

    this.updateClasses(myClasses);

    return myClasses;
  }

  async getLevels() {
    //Assumption is all getter functions will fail from client side if not authorised
    return wrap(await supabase.from("Level").select("*"));
  }

  async addLevel(name: string, hskDifficulty: number) {
    const newLevel = wrap(
      await supabase
        .from("Level")
        .insert({
          name,
          hskDifficulty,
        })
        .select()
    )[0];

    return newLevel;
  }

  async addTopic(name: string, levelId: number) {
    if (!this.staffDetail.isAdmin) {
      throw new Error("A staff needs to be an admin to create new topics!");
    }

    const result = wrap(
      await supabase
        .from("Topic")
        .insert({
          name,
          level: levelId,
        })
        .select()
    )[0];

    return result;
  }

  async addModule(topicId: number, moduleName: string) {
    const { id } = wrap(
      await supabase
        .from("Module")
        .insert({
          name: moduleName,
          topic: topicId,
          createdBy: this.teacherAccount.id,
        })
        .select()
    )[0];

    const module = new Module();
    await module.init(id);
    return module;
  }

  async getModules() {
    const moduleIDs = wrap(await supabase.from("Module").select("id"));

    const modules = Promise.all(
      moduleIDs.map(async ({ id }) => {
        const module = new Module();
        await module.init(id);
        return module;
      })
    );

    return modules;
  }

  async getEngagementPanels() {
    const ePanelIds = wrap(await supabase.from("EngagementPanel").select("id"));

    const engagementPanels = Promise.all(
      ePanelIds.map(async ({ id }) => {
        const ePanel = new EngagementPanel();
        await ePanel.init(id);
        return ePanel;
      })
    );

    return engagementPanels;
  }

  async setDisplayName(displayName: string) {
    //TODO: Only admin accounts should be able to approve teachers
    await supabase
      .from("StaffDetails")
      .update({ displayName })
      .match({ id: this.staffDetail.id });

    await this.pullUserDetails();

    return this;
  }

  async getTopics() {
    return wrap(await supabase.from("Topic").select("*"));
  }

  async removeTopic(topicId: number) {
    if (!this.staffDetail.isAdmin) {
      throw new Error("A staff needs to be an admin to remove new topics!");
    }
    await supabase.from("Topic").delete().match({ id: topicId }).throwOnError();
    return this;
  }

  //TODO: Make proper error system with fixed set of error codes
  async createClass(levelId: number, startDate: Date, endDate: Date) {
    if (!this.staffDetail.isAdmin) {
      throw new Error("A staff needs to be an admin to create new classes!");
    }

    const { id } = wrap(
      await supabase
        .from("Class")
        .insert({
          level: levelId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          createdByStaff: this.staffDetail.id!,
        })
        .select()
    )[0];

    const newClass = new Class();
    await newClass.init(id);
    //TODO: Add test case for adding a teacher to a class
    // await newClass.addTeacher(this.teacherAccount.id!);
    await this.pullUserDetails();
    return newClass;
  }

  async getTopicsByLevel(levelId: number) {
    return wrap(
      await supabase.from("Topic").select("*").match({ level: levelId })
    );
  }

  async signOut() {
    await supabase.auth.signOut();
    this.user = {};
    this.staffDetail = {};
    return this;
  }

  async approveTeacher() {
    //TODO: Only admin accounts should be able to approve teachers
    await supabase
      .from("StaffDetails")
      .update({ isApproved: true })
      .match({ id: this.staffDetail.id })
      .throwOnError();

    await this.pullUserDetails();

    return this;
  }

  async setTeacherAdmin(isAdmin: boolean = true) {
    //TODO: Only admin accounts should be able to approve teachers
    await supabase
      .from("StaffDetails")
      .update({ isAdmin })
      .match({ id: this.staffDetail.id })
      .throwOnError();

    await this.pullUserDetails();

    return this;
  }

  async emailSignUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) {
    //TODO: Add rollback if failed
    const {
      data: { user },
      error,
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${process.env.REACT_APP_FRONTEND_URL}/email-confirmed`,
      },
    });

    if (error) throw error;
    if (!user) throw new Error("Sign up failed!");

    this.user = user;

    const staffDetail: Partial<definitions["StaffDetails"]> = {
      brijEmail: email,
      displayName: `${firstName} ${lastName}`,
    };

    const resultUser = wrap(
      await supabase.from("StaffDetails").insert(staffDetail).select()
    );

    this.staffDetail = resultUser[0];

    const teacher: Partial<definitions["TeacherAccount"]> = {
      staffDetail: this.staffDetail.id,
      account: user.id,
    };

    const resultAccount = wrap(
      await supabase.from("TeacherAccount").insert(teacher).select()
    );

    this.teacherAccount = resultAccount[0];

    return this;
  }

  async emailSignIn(email: string, password: string) {
    const {
      data: { user },
      error,
    } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    //TODO: Make both error objects same shape
    if (!user) throw new Error("User Sign in failed!");

    this.user = user;

    //TODO: Instead of pulling user details can listen for DB updates
    await this.pullUserDetails();

    if (this.teacherAccount?.id === undefined)
      throw new Error("User Sign in failed!");

    return this;
  }

  private setTeacherAccount(
    newTeacherAccount: Partial<definitions["TeacherAccount"]>
  ) {
    this.teacherAccount = newTeacherAccount;
  }

  private setStaffDetail(newStaffDetail: Partial<definitions["StaffDetails"]>) {
    this.staffDetail = newStaffDetail;
  }

  async pullUserDetails() {
    const userMeta = await supabase.auth.getUser();
    if (!userMeta.data.user) return;
    this.user = userMeta.data.user;
    //TODO: Ensure function doesn't crash when pulling non-existent accounts (Perhaps after deletion for example)
    //TODO: Pull all at once to reduce lag
    this.setTeacherAccount(
      wrap(
        await supabase
          .from("TeacherAccount")
          .select("*")
          .match({ account: this.user.id })
      )[0]
    );
    if (this.teacherAccount) {
      this.setStaffDetail(
        wrap(
          await supabase
            .from("StaffDetails")
            .select("*")
            .match({ id: this.teacherAccount.staffDetail })
        )[0]
      );

      if (!this.staffDetail.isApproved) {
        this.teacherAccount = {};
        this.staffDetail = {};
        //TODO: Handle validation in backend as well
        throw new Error("Teacher account must be approved before logging in!");
      }

      await this.getMyClasses();
    }

    return this;
  }
}

export default new Teacher();
